import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Star, Users, Target, Clock, Heart } from 'lucide-react';
const milestones = [{
  year: '2003',
  title: '平台创立',
  desc: '新加坡首个按摩推荐社区上线'
}, {
  year: '2010',
  title: '突破10,000注册用户',
  desc: '建立行业首个技师认证体系'
}, {
  year: '2018',
  title: '智能匹配系统上线',
  desc: 'AI驱动个性化推荐'
}, {
  year: '2024',
  title: '50,000+用户里程碑',
  desc: '覆盖全岛2,000+认证技师'
}];
const values = [{
  icon: Shield,
  text: '安全至上 (Safety First)'
}, {
  icon: Star,
  text: '品质保证 (Quality Assured)'
}, {
  icon: Users,
  text: '用户为本 (User-Centric)'
}];
export function AboutSection() {
  return <section id="about-story" className="py-24 bg-white relative overflow-hidden" data-id="element-31">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-32">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-[#FDF8F0] rounded-bl-full opacity-50" data-id="element-33"></div>
        <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-[#2D5A3D]/5 rounded-tr-full" data-id="element-34"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-35">
        {/* Section Header */}
        <div className="text-center mb-16" data-id="element-36">
          <motion.span initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="text-[#C9A96E] font-medium tracking-widest uppercase text-sm" data-id="element-37">
            Our Story
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif font-bold text-[#2D5A3D] mt-2 mb-6" data-id="element-38">
            关于新加坡爽吧 Shiok8
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-[#666] text-lg max-w-3xl mx-auto leading-relaxed" data-id="element-39">
            「Shiok」源自新加坡本地俚语，意为「爽、舒服、过瘾」。自2003年创立以来，我们始终以这份对舒适的追求为核心，打造新加坡最值得信赖的按摩推荐平台。
          </motion.p>
        </div>

        {/* Story & Mission Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24" data-id="element-40">
          {/* Left Column: Platform Story */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-41">
            <div className="flex items-center space-x-3 mb-6" data-id="element-42">
              <div className="w-10 h-10 rounded-full bg-[#2D5A3D]/10 flex items-center justify-center text-[#2D5A3D]" data-id="element-43">
                <Clock size={20} data-id="element-44" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-[#1A1A1A]" data-id="element-45">
                从一个简单的想法开始
              </h3>
            </div>
            <div className="space-y-6 text-[#4A4A4A] leading-relaxed" data-id="element-46">
              <p data-id="element-47">
                2003年，新加坡的按摩行业缺乏透明度和标准化。创始团队深感消费者在选择按摩服务时面临信息不对称的困扰——不知道哪位技师靠谱，不了解服务质量如何。于是，新加坡爽吧应运而生，致力于搭建一座连接优质技师与消费者的桥梁。
              </p>
              <p data-id="element-48">
                经过20多年的深耕，我们已从一个小型推荐社区成长为新加坡最大、最活跃的按摩服务平台，服务覆盖全岛各区。
              </p>
            </div>
          </motion.div>

          {/* Right Column: Mission & Values */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-[#FDF8F0] p-8 md:p-10 rounded-2xl border border-[#D4B483]/20 shadow-sm" data-id="element-49">
            <div className="flex items-center space-x-3 mb-6" data-id="element-50">
              <div className="w-10 h-10 rounded-full bg-[#C9A96E]/20 flex items-center justify-center text-[#C9A96E]" data-id="element-51">
                <Target size={20} data-id="element-52" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-[#2D5A3D]" data-id="element-53">
                我们的使命
              </h3>
            </div>
            <p className="text-[#4A4A4A] leading-relaxed mb-8" data-id="element-54">
              让每一位新加坡居民都能轻松找到安全、专业、适合自己的按摩服务。我们相信，优质的按摩不仅是身体的放松，更是心灵的疗愈。
            </p>

            <div className="flex flex-wrap gap-4" data-id="element-55">
              {values.map((value, index) => <div key={index} className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full border border-[#D4B483]/30 shadow-sm" data-id="element-56">
                  <value.icon className="w-4 h-4 text-[#2D5A3D]" data-id="element-57" />
                  <span className="text-sm font-medium text-[#1A1A1A]" data-id="element-58">
                    {value.text}
                  </span>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* Milestones Timeline */}
        <div className="mb-24" data-id="element-59">
          <h3 className="text-2xl font-serif font-bold text-center text-[#1A1A1A] mb-12" data-id="element-60">
            发展历程 (Milestones)
          </h3>
          <div className="relative" data-id="element-61">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-[#F5EDE0] -translate-y-1/2 z-0" data-id="element-62"></div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative z-10" data-id="element-63">
              {milestones.map((milestone, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.15
            }} className="relative pl-8 md:pl-0 md:text-center" data-id="element-64">
                  {/* Mobile Timeline Line */}
                  <div className="md:hidden absolute left-[11px] top-0 bottom-[-32px] w-0.5 bg-[#F5EDE0] z-0 last:hidden" data-id="element-65"></div>

                  {/* Timeline Dot */}
                  <div className="absolute left-0 md:left-1/2 top-1 md:top-1/2 w-6 h-6 bg-white border-4 border-[#2D5A3D] rounded-full -translate-x-1/2 md:-translate-y-1/2 z-10 shadow-md" data-id="element-66"></div>

                  <div className="md:pt-12 md:pb-0 pb-8" data-id="element-67">
                    <div className="text-2xl font-serif font-bold text-[#C9A96E] mb-2" data-id="element-68">
                      {milestone.year}
                    </div>
                    <h4 className="text-lg font-bold text-[#1A1A1A] mb-1" data-id="element-69">
                      {milestone.title}
                    </h4>
                    <p className="text-sm text-[#666]" data-id="element-70">{milestone.desc}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* Closing Quote */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center bg-[#2D5A3D] text-white py-12 px-6 rounded-2xl shadow-xl relative overflow-hidden" data-id="element-71">
          <div className="absolute top-0 right-0 opacity-10" data-id="element-72">
            <Heart className="w-64 h-64 -mt-16 -mr-16" data-id="element-73" />
          </div>
          <div className="relative z-10" data-id="element-74">
            <p className="text-2xl md:text-3xl font-serif font-bold mb-4" data-id="element-75">
              二十年如一日，只为您的每一次「爽」体验。
            </p>
            <p className="text-[#C9A96E] text-lg font-medium tracking-wide" data-id="element-76">
              Two decades of dedication, for every moment of Shiok.
            </p>
          </div>
        </motion.div>

        {/* SEO Rich Content Block */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mt-20 bg-[#FDF8F0] rounded-2xl p-8 md:p-12 border border-[#D4B483]/15" data-id="element-77">
          <h3 className="text-2xl font-serif font-bold text-[#2D5A3D] mb-6" data-id="element-78">
            新加坡爽吧 Shiok8 — 您的专属按摩推荐平台
          </h3>
          <div className="space-y-5 text-[#4A4A4A] leading-relaxed text-sm md:text-base" data-id="element-79">
            <p data-id="element-80">
              <strong className="text-[#1A1A1A]" data-id="element-81">新加坡爽吧(Shiok8)</strong>
              是新加坡成立最早、规模最大的专业按摩推荐平台。自2003年上线以来，平台始终专注于为新加坡本地居民及游客提供高品质的按摩服务信息与预约推荐。无论您身处
              <strong className="text-[#2D5A3D]" data-id="element-82">
                芽笼(Geylang)、乌节路(Orchard
                Road)、牛车水(Chinatown)、武吉士(Bugis)、克拉码头(Clarke
                Quay)、小印度(Little India)
              </strong>
              还是全岛任何区域，都能通过我们的平台轻松找到附近最优质的按摩服务。
            </p>
            <p data-id="element-83">
              我们的服务涵盖
              <strong className="text-[#2D5A3D]" data-id="element-84">
                传统中式推拿、泰式按摩、精油SPA、足底按摩(脚底按摩)、肩颈按摩、全身按摩、深层组织按摩、热石按摩、淋巴排毒按摩、产后按摩、运动康复按摩
              </strong>
              等多种类型。每一位在平台上推荐的按摩技师都经过严格的身份验证和专业资质审核，确保您享受到安全、专业、舒适的按摩体验。
            </p>
            <p data-id="element-85">
              作为新加坡按摩行业的标杆平台，新加坡爽吧拥有超过
              <strong className="text-[#2D5A3D]" data-id="element-86">50,000名注册用户</strong>和
              <strong className="text-[#2D5A3D]" data-id="element-87">2,000多名认证按摩技师</strong>
              ，每月成功促成超过
              <strong className="text-[#2D5A3D]" data-id="element-88">10,000次按摩预约</strong>
              。平台支持<strong className="text-[#2D5A3D]" data-id="element-89">上门按摩服务</strong>
              和<strong className="text-[#2D5A3D]" data-id="element-90">到店按摩预约</strong>
              两种模式，满足不同用户的需求偏好。
            </p>
            <p data-id="element-91">
              我们的智能匹配推荐系统会根据您的位置、服务偏好、预算范围和历史评价，为您精准推荐最合适的按摩技师。所有用户评价均为真实体验反馈，帮助您做出最明智的选择。无论您是寻求
              <strong className="text-[#2D5A3D]" data-id="element-92">
                日常减压放松、养生保健、亚健康调理
              </strong>
              ，还是
              <strong className="text-[#2D5A3D]" data-id="element-93">
                运动后肌肉恢复、慢性疼痛缓解
              </strong>
              ，新加坡爽吧都能为您匹配最专业的按摩方案。
            </p>
            <p data-id="element-94">
              选择新加坡爽吧Shiok8，选择20年品质保证。立即浏览我们的平台，开启您的舒适之旅，体验真正的「爽」！
            </p>
          </div>

          <div className="mt-8 flex flex-wrap gap-2" data-id="element-95">
            {['新加坡按摩', '新加坡推拿', '新加坡SPA', '精油按摩', '足底按摩', '肩颈按摩', '全身按摩', '泰式按摩', '上门按摩', '到店预约', '深层组织按摩', '热石按摩', '芽笼按摩', '乌节路SPA', '牛车水推拿', '养生保健', '减压放松', '运动康复', '产后按摩', '淋巴排毒'].map(tag => <span key={tag} className="inline-block bg-white text-[#2D5A3D] text-xs font-medium px-3 py-1.5 rounded-full border border-[#2D5A3D]/15" data-id="element-96">
                {tag}
              </span>)}
          </div>
        </motion.div>
      </div>
    </section>;
}